import { useMemo, useState } from "react"
import { Box, Button, Divider, IconButton, InputAdornment, Skeleton, Stack, TextField, Typography } from "@mui/material"
import { DialogBox } from "lib/@components/dialogBox"
import { Bound } from "lib/@components/binding/Bound"
import { StandardSwitch } from "lib/@components/binding/bound-components"
import { PagedRepeat } from "lib/@components/paged-repeat"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { NoResults } from "lib/@components/no-results/no-results"
import { createSlot } from "lib/@components/slot/create-slot"
import Iconify from "minimals-template/components/Iconify"
import { useAuthoringLegislationLibrary } from "authoring/routes/legislation-library/use-authoring-legislation-library"
import { LEGISLATION, LEGISLATION_FOLDER } from "authoring/routes/legislation-library/legislation-library-helpers"
import { AuthoringLegislationSelectorItem } from "./authoring-legislation-selector-item"
import { AuthoringLegislationSelectorDefaultSummary } from "./authoring-legislation-selector-default-summary"

export const AuthoringLegislationSelectorSummary = createSlot("AuthoringLegislationSelectorSummary")
AuthoringLegislationSelectorSummary.plug(<AuthoringLegislationSelectorDefaultSummary $lazy />)

export function AuthoringLegislationSelectorDialog({
    group,
    initialSelection,
    tickableType = LEGISLATION,
    getSelectionKey = defaultSelectionKey,
    minSelected = 0,
    minSelectedReason = "At least one is required",
    title = "References",
    rootLabel = "References",
    summaryVariant,
    showFolderPath = false,
    serializeSelection = (selected) => [...selected].map(Number),
}) {
    const { isLoading, state, getNode, getChildren, getRootIds, getPath } = useAuthoringLegislationLibrary()
    const [folderId, setFolderId] = useState(null)
    const [search, setSearch] = useState("")
    const [showSelectedOnly, setShowSelectedOnly] = useState(false)

    const initialSet = useMemo(computeInitialSet, [initialSelection, group])
    const [selected, setSelected] = useState(initialSet)

    const atRoot = !folderId
    const currentFolder = folderId ? getNode(folderId) : null
    const addedCount = countAdded()
    const removedCount = countRemoved()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const list = useMemo(computeList, [isLoading, folderId, search, showSelectedOnly, selected, state])

    return (
        <DialogBox
            title={title}
            okCaption="Save"
            okDisabled={addedCount + removedCount === 0}
            value={serializeSelection(selected)}
            data-cy="legislation-selector-dialog"
        >
            <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <If truthy={!atRoot}>
                            <Button
                                size="small"
                                onClick={handleBack}
                                data-cy="previous-folder-button"
                                startIcon={<Iconify icon="mdi:arrow-up" />}
                            >
                                <Typography variant="subtitle2" color="text.secondary" data-cy="previous-folder-title">
                                    {parentTitle()}
                                </Typography>
                            </Button>
                        </If>
                        <Typography variant="subtitle2" data-cy="folder-title">
                            {currentFolderTitle()}
                        </Typography>
                    </Stack>
                    <Box flex={1} />
                    <StandardSwitch
                        value={showSelectedOnly}
                        label={`Show Selected Only (${selected.size})`}
                        onChange={setShowSelectedOnly}
                        data-cy="show-selected-legislation"
                    />
                </Stack>
                <Bound
                    addedCount={addedCount}
                    removedCount={removedCount}
                    initialCount={initialSet.size}
                    addedNodes={addedNodes()}
                    removedNodes={removedNodes()}
                    onToggle={handleToggle}
                    getPath={getPath}
                    isLoading={isLoading}
                >
                    <AuthoringLegislationSelectorSummary.Slot type={summaryVariant} />
                </Bound>
                <TextField
                    data-cy="search-input"
                    placeholder="Search"
                    value={search}
                    onChange={({ target }) => setSearch(target.value)}
                    size="small"
                    variant="standard"
                    fullWidth
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="Clear search"
                                        edge="end"
                                        size="small"
                                        onClick={() => setSearch("")}
                                        disabled={!search}
                                    >
                                        <Iconify icon="material-symbols:close-rounded" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                <Divider />
                <If truthy={isLoading}>
                    <Stack>
                        <Repeat
                            list={Array.from({ length: 5 })}
                            item={<Skeleton sx={{ margin: 0, height: "42px" }} />}
                        />
                    </Stack>
                    <Else>
                        <If truthy={list.length}>
                            <PagedRepeat
                                list={list}
                                keyFn={keyForNode}
                                item={
                                    <AuthoringLegislationSelectorItem
                                        onSelect={handleNavigate}
                                        onToggle={handleToggle}
                                        selected={selected}
                                        isTickable={isTickable}
                                        getSelectionKey={getSelectionKey}
                                        disabledReason={computeDisabledReason}
                                        getPath={showFolderPath ? getPath : undefined}
                                    />
                                }
                            />
                            <Else>
                                <NoResults />
                            </Else>
                        </If>
                    </Else>
                </If>
            </Stack>
        </DialogBox>
    )

    function isTickable(node) {
        return node?.type === tickableType
    }

    // Reactive to the current tentative selection — disables unticking once doing so would drop
    // the total below minSelected, so e.g. an item's last remaining folder can't be unticked.
    function computeDisabledReason(node) {
        const key = getSelectionKey(node)
        if (key == null || !selected.has(key)) return null
        if (selected.size > minSelected) return null
        return minSelectedReason
    }

    function handleNavigate(node) {
        if (node.type === LEGISLATION_FOLDER) setFolderId(node.id)
    }

    function handleToggle(node) {
        const key = getSelectionKey(node)
        if (key == null) return
        setSelected((current) => {
            const next = new Set(current)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })
    }

    function handleBack() {
        if (atRoot) return
        const path = getPath(folderId)
        const parent = path[path.length - 2]
        setFolderId(parent ? parent.id : null)
    }

    function computeList() {
        if (isLoading) return []
        const term = search.trim().toLowerCase()
        if (showSelectedOnly) return selectedNodes()
        if (term) return searchTickable(term)
        if (atRoot) return getRootIds().map(getNode).filter(Boolean)
        return getChildren(folderId)
    }

    function searchTickable(term) {
        const results = []
        for (const node of state.lookup.values()) {
            if (!isTickable(node)) continue
            const label = node.title ?? node.name
            if (label?.toLowerCase().includes(term)) results.push(node)
        }
        return results
    }

    function selectedNodes() {
        const byKey = tickableByKey()
        const nodes = []
        for (const key of selected) {
            const node = byKey.get(key)
            if (node) nodes.push(node)
        }
        return nodes
    }

    function tickableByKey() {
        const map = new Map()
        for (const node of state.lookup.values()) {
            if (!isTickable(node)) continue
            const key = getSelectionKey(node)
            if (key != null) map.set(key, node)
        }
        return map
    }

    function computeInitialSet() {
        const set = new Set()
        for (const id of initialSelection ?? group?.legislation ?? []) {
            if (id == null) continue
            set.add(String(id))
        }
        return set
    }

    function countAdded() {
        let count = 0
        for (const key of selected) if (!initialSet.has(key)) count++
        return count
    }

    function countRemoved() {
        let count = 0
        for (const key of initialSet) if (!selected.has(key)) count++
        return count
    }

    function addedNodes() {
        const byKey = tickableByKey()
        const nodes = []
        for (const key of selected) {
            if (initialSet.has(key)) continue
            const node = byKey.get(key)
            if (node) nodes.push(node)
        }
        return nodes
    }

    function removedNodes() {
        const byKey = tickableByKey()
        const nodes = []
        for (const key of initialSet) {
            if (selected.has(key)) continue
            const node = byKey.get(key)
            if (node) nodes.push(node)
        }
        return nodes
    }

    function currentFolderTitle() {
        if (atRoot) return rootLabel
        return currentFolder?.name ?? rootLabel
    }

    function parentTitle() {
        const path = getPath(folderId)
        const parent = path[path.length - 2]
        return parent?.name ?? rootLabel
    }

    function keyForNode(node) {
        return node.id
    }
}

function defaultSelectionKey(node) {
    return node?.legacyId != null ? String(node.legacyId) : null
}
